/* eslint-disable new-cap */
/**
 * REST API を使用したメール取得 hook
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Enum Types
// =====================================

export const RecruitmentType = {
  Matter: "Matter" as const,
  HumanResource: "HumanResource" as const,
};
export type RecruitmentType = "Matter" | "HumanResource";

export const OrderType = {
  DateAsc: "DateAsc" as const,
  DateDesc: "DateDesc" as const,
  AgeAsc: "AgeAsc" as const,
  AgeDesc: "AgeDesc" as const,
  RewardAsc: "RewardAsc" as const,
  RewardDesc: "RewardDesc" as const,
};
export type OrderType =
  | "DateAsc"
  | "DateDesc"
  | "AgeAsc"
  | "AgeDesc"
  | "RewardAsc"
  | "RewardDesc";

export const GenderType = {
  Male: "Male" as const,
  Female: "Female" as const,
};
export type GenderType = "Male" | "Female";

// =====================================
// Input Types (検索フィルター)
// =====================================

export interface AgeRangeInput {
  from?: number | null;
  to?: number | null;
}

export interface RewardRangeInput {
  from?: number | null;
  to?: number | null;
}

export interface HumanResourceTypes {
  freerance?: boolean;
  contractEmployee?: boolean;
  fullTimeEmployee?: boolean;
}

export interface HumanResourceFilter {
  genderType?: GenderType | null;
  ageRange?: AgeRangeInput | null;
  rewardRange?: RewardRangeInput | null;
  hrTypes?: HumanResourceTypes | null;
  immediately?: boolean | null;
  needRemote?: boolean | null;
  onlyJapanese?: boolean | null;
}

export interface MatterFilter {
  rewardRange?: RewardRangeInput | null;
  distanceFromEnd?: number | null;
  oneOpportunity?: boolean;
  otherCountriesEnabled?: boolean;
  onlyEnableFreelance?: boolean;
  enableRemote?: boolean;
}

export interface EmailSearchInput {
  recruitmentType?: RecruitmentType | null;
  hrFilter?: HumanResourceFilter | null;
  matterFilter?: MatterFilter | null;
  limitedByDay?: number;
  orderType?: OrderType;
  freeWords?: string[] | null;
}

// =====================================
// Entity Types
// =====================================

export interface EmailFrom {
  address: string;
  name: string;
}

export interface HumanResourceColumn {
  age?: string | null;
  ageInt?: number | null;
  gender?: string | null;
  station?: string | null;
  hrTypes?: string | null;
  startedDate?: string | null;
  reward?: string | null;
  rewardInt?: number | null;
  isRemoteNeeded?: boolean | null;
  remoteFrequency?: string | null;
  technology?: string[];
  industryCategory?: string | null;
  isJapanese?: boolean | null;
  isDirect?: boolean | null;
  isMultiple?: boolean | null;
}

export interface MatterColumn {
  station?: string | null;
  isRemoteEnabled?: boolean | null;
  period?: string | null;
  reward?: string | null;
  rewardInt?: number | null;
  workingTimeWidth?: string | null;
  otherCountriesEnabled?: boolean | null;
  age?: string | null;
  gender?: string | null;
  numberOfInterviews?: string | null;
  commercialDistributionLimitation?: string | null;
  endOrPrimeContractorDirect?: string | null;
  remoteFrequency?: string | null;
  technology?: string[];
  industryCategory?: string | null;
  summary?: string | null;
  detail?: string | null;
  workingHours?: string | null;
  reqcuitmentRequired?: number | null;
  interviewMethod?: string | null;
  isCountrysideEnabled?: boolean | null;
  freelanceEnabled?: boolean | null;
  paymentSite?: number | null;
  developmentEnvironment?: string[];
  requiredSkills?: string[];
  niceToHaveSkills?: string[];
  remarks?: string | null;
}

export type AttachmentItem = {
  filename: string;
  s3Key: string;
  contentType: string;
  size: number;
  isInline: boolean;
};

export interface EmailDocument {
  id: string;
  date: string;
  subject: string;
  content: string;
  from: EmailFrom;
  recruitmentType: RecruitmentType;
  hrColumn?: HumanResourceColumn | null;
  matterColumn?: MatterColumn | null;
  attachments?: AttachmentItem[];
  hasAttachments?: boolean;
  duplicateCount?: number;
}

export interface EmailDocuments {
  totalCount: number;
  nodes: EmailDocument[];
}

// =====================================
// REST API Hook Types
// =====================================

export interface EmailsResponse {
  emails: EmailDocuments;
}

export interface UseEmailsRestApiOptions {
  variables: {
    filter: EmailSearchInput | null;
    offset: number;
    limit: number;
  };
  onCompleted?: () => void;
  notifyOnNetworkStatusChange?: boolean;
}

export interface UseEmailsRestApiResult {
  data: EmailsResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (variables?: {
    filter: EmailSearchInput | null;
    offset: number;
    limit: number;
  }) => Promise<EmailsResponse>;
}

/**
 * メール一覧を REST API から取得する hook
 * @param {UseEmailsRestApiOptions} options - フック設定オプション
 * @return {UseEmailsRestApiResult} メールデータ、エラー、ローディング状態、再取得関数を含むオブジェクト
 */
export function useEmailsRestApi(
  options: UseEmailsRestApiOptions,
): UseEmailsRestApiResult {
  const [data, setData] = useState<EmailsResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchEmails = useCallback(
    async (variables: {
      filter: EmailSearchInput | null;
      offset: number;
      limit: number;
    }): Promise<EmailsResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {
          offset: String(variables.offset),
          limit: String(variables.limit),
        };

        if (variables.filter) {
          params.filter = JSON.stringify(variables.filter);
        }

        const responseData = await restClient.get<EmailDocuments>(
          "/emails",
          params,
        );

        const result: EmailsResponse = {
          emails: {
            totalCount: responseData.totalCount,
            nodes: responseData.nodes.map(doc => ({
              ...doc,
              recruitmentType: doc.recruitmentType as RecruitmentType,
            })),
          },
        };

        setData(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    },
    [options.onCompleted],
  );

  const initialFetchDone = useRef(false);
  const variablesRef = useRef(options.variables);

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchEmails(variablesRef.current).catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchEmails]);

  const refetch = useCallback(
    async (variables?: {
      filter: EmailSearchInput | null;
      offset: number;
      limit: number;
    }): Promise<EmailsResponse> => {
      return fetchEmails(variables || options.variables);
    },
    [fetchEmails, options.variables],
  );

  return {
    data,
    error,
    loading,
    refetch,
  };
}

export default useEmailsRestApi;
